/** Portfolio page: lists positions and cash balance for a selected portfolio. */

import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { apiClient } from "@/api/client";
import DataTable, { DataTableColumn } from "@/components/tables/DataTable";
import StatCard from "@/components/cards/StatCard";
import type { AxiosError } from "axios";
import type { Portfolio as PortfolioType, Position } from "@/types";

const columns: DataTableColumn<Position>[] = [
  { header: "Symbol", accessor: (row) => <span className="font-mono">{row.symbol}</span> },
  { header: "Quantity", accessor: (row) => row.quantity, align: "right" },
  { header: "Avg. entry", accessor: (row) => `$${row.average_entry_price}`, align: "right" },
];

export default function Portfolio(): React.ReactElement {
  const [portfolios, setPortfolios] = useState<PortfolioType[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selected, setSelected] = useState<PortfolioType | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newName, setNewName] = useState("");
  const [showPositionForm, setShowPositionForm] = useState(false);
  const [posSymbol, setPosSymbol] = useState("");
  const [posQuantity, setPosQuantity] = useState("");
  const [posPrice, setPosPrice] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const loadPortfolios = async (): Promise<void> => {
    setIsLoading(true);
    try {
      const { data } = await apiClient.get<PortfolioType[]>("/portfolios");
      setPortfolios(data);
      if (data.length > 0 && !selectedId) setSelectedId(data[0].id);
    } catch {
      setError("Unable to load portfolios.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void loadPortfolios();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!selectedId) {
      setSelected(null);
      return;
    }
    apiClient
      .get<PortfolioType>(`/portfolios/${selectedId}`)
      .then(({ data }) => setSelected(data))
      .catch(() => setError("Unable to load this portfolio."));
  }, [selectedId]);

  const handleCreatePortfolio = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setIsSubmitting(true);
    try {
      const { data } = await apiClient.post<PortfolioType>("/portfolios", { name: newName });
      setNewName("");
      setShowCreateForm(false);
      await loadPortfolios();
      setSelectedId(data.id);
    } catch {
      setError("Unable to create portfolio.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAddPosition = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (!selectedId) return;
    setIsSubmitting(true);
    try {
      await apiClient.put(`/portfolios/${selectedId}/positions`, {
        symbol: posSymbol,
        quantity: posQuantity,
        average_entry_price: posPrice,
      });
      setPosSymbol("");
      setPosQuantity("");
      setPosPrice("");
      setShowPositionForm(false);
      const { data } = await apiClient.get<PortfolioType>(`/portfolios/${selectedId}`);
      setSelected(data);
    } catch (err) {
      const axiosError = err as AxiosError<{ detail: string }>;
      setError(axiosError.response?.data?.detail ?? "Unable to add position.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalPositionValue = (selected?.positions ?? []).reduce(
    (sum, p) => sum + Number(p.quantity) * Number(p.average_entry_price),
    0
  );

  if (isLoading) {
    return <p className="text-sm text-mist-400">Loading portfolios…</p>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Portfolio</h1>
          <p className="mt-1 text-sm text-mist-400">Your holdings and cash balance.</p>
        </div>
        <div className="flex items-center gap-3">
          {portfolios.length > 0 && (
            <select
              value={selectedId ?? ""}
              onChange={(e) => setSelectedId(e.target.value)}
              className="field-input w-48"
            >
              {portfolios.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          )}
          <button className="btn-secondary" onClick={() => setShowCreateForm((v) => !v)}>
            New portfolio
          </button>
          {selected && (
            <button className="btn-primary" onClick={() => setShowPositionForm((v) => !v)}>
              Add position
            </button>
          )}
        </div>
      </div>

      {error && <p className="text-sm text-signal-rose">{error}</p>}

      {showCreateForm && (
        <form onSubmit={handleCreatePortfolio} className="panel flex items-end gap-3 p-5">
          <div className="flex-1">
            <label className="field-label">Portfolio name</label>
            <input
              required
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="field-input"
              placeholder="Growth Portfolio"
            />
          </div>
          <button type="submit" disabled={isSubmitting} className="btn-primary">
            Create
          </button>
        </form>
      )}

      {showPositionForm && selected && (
        <form onSubmit={handleAddPosition} className="panel grid grid-cols-1 gap-3 p-5 sm:grid-cols-4">
          <input
            required
            value={posSymbol}
            onChange={(e) => setPosSymbol(e.target.value)}
            className="field-input"
            placeholder="Symbol (AAPL)"
          />
          <input
            required
            value={posQuantity}
            onChange={(e) => setPosQuantity(e.target.value)}
            className="field-input"
            placeholder="Quantity"
            inputMode="decimal"
          />
          <input
            required
            value={posPrice}
            onChange={(e) => setPosPrice(e.target.value)}
            className="field-input"
            placeholder="Avg. entry price"
            inputMode="decimal"
          />
          <button type="submit" disabled={isSubmitting} className="btn-primary">
            Save
          </button>
        </form>
      )}

      {portfolios.length === 0 ? (
        <div className="panel flex flex-col items-center gap-2 py-16 text-center">
          <p className="text-sm font-medium text-mist-200">No portfolios yet</p>
          <p className="text-sm text-mist-400">Create one above to start tracking positions.</p>
        </div>
      ) : (
        selected && (
          <>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <StatCard label="Cost basis value" value={`$${totalPositionValue.toLocaleString()}`} />
              <StatCard label="Cash balance" value={`$${Number(selected.cash_balance).toLocaleString()}`} />
              <StatCard label="Open positions" value={String(selected.positions.length)} />
            </div>

            <DataTable<Position>
              columns={columns}
              rows={selected.positions}
              rowKey={(row) => row.id}
              emptyMessage="No positions yet. Add one above."
            />

            <Link to="/risk" className="inline-block text-sm text-signal-teal hover:underline">
              View this portfolio's risk profile →
            </Link>
          </>
        )
      )}
    </div>
  );
}
